import { supabase } from '@/lib/supabase';
import { restHeaders } from '@/lib/supabaseRest';
import { cleanSearchTerm, ilikeValue } from '@/lib/searchPattern';
import type { Database } from '@/types/database';

type Property = Database['public']['Tables']['properties']['Row'];
type PropertyInsert = Database['public']['Tables']['properties']['Insert'];
type PropertyUpdate = Database['public']['Tables']['properties']['Update'];

export type { Property, PropertyInsert, PropertyUpdate };

export interface PropertyFilters {
  status?: string[];
  property_type?: string[];
  pipeline_stage?: string[];
  assigned_to?: string;
  search?: string;
}

export interface PropertyListResponse {
  data: Property[];
  count: number;
}

/**
 * Fetch all properties with optional filters and pagination
 */
export async function getProperties(
  filters?: PropertyFilters,
  page = 1,
  pageSize = 50
): Promise<PropertyListResponse> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    // Build query string
    let queryString = 'select=*&order=created_at.desc';
    
    // Apply filters
    if (filters?.status && filters.status.length > 0) {
      queryString += `&status=in.(${filters.status.join(',')})`;
    }
    
    if (filters?.property_type && filters.property_type.length > 0) {
      queryString += `&property_type=in.(${filters.property_type.join(',')})`;
    }
    
    if (filters?.pipeline_stage && filters.pipeline_stage.length > 0) {
      queryString += `&pipeline_stage=in.(${filters.pipeline_stage.join(',')})`;
    }
    
    if (filters?.assigned_to) {
      queryString += `&assigned_to=eq.${filters.assigned_to}`;
    }
    
    // `*term*`, never `%term%`: see lib/searchPattern. New Offer's, Send NDA's and Compare's
    // property pickers all search through here.
    const term = cleanSearchTerm(filters?.search);
    if (term) {
      const searchPattern = ilikeValue(term);
      queryString += `&or=(name.ilike.${searchPattern},address_street.ilike.${searchPattern},address_city.ilike.${searchPattern})`;
    }
    
    // Build pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Use direct fetch since Supabase client doesn't work in Cursor browser
    const response = await fetch(`${supabaseUrl}/rest/v1/properties?${queryString}`, {
      headers: await restHeaders({
        'Range': `${from}-${to}`,
        'Prefer': 'count=exact'
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const contentRange = response.headers.get('content-range');
    const count = contentRange ? parseInt(contentRange.split('/')[1]) : data.length;
    
    return {
      data: data || [],
      count
    };
  } catch (error) {
    console.error('Error in getProperties:', error);
    throw error;
  }
}

/**
 * Fetch a single property by ID with all related data
 */
export async function getPropertyById(id: string): Promise<Property | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    const response = await fetch(`${supabaseUrl}/rest/v1/properties?id=eq.${id}&select=*`, {
      headers: await restHeaders({
        'Accept': 'application/json' // Changed from application/vnd.pgrst.object+json to avoid 406 errors
      })
    });
    
    // Handle 406 Not Acceptable (property not found or no access)
    if (response.status === 406) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // PostgREST returns an array, so get the first item
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error in getPropertyById:', error);
    throw error;
  }
}

/**
 * Create a new property
 */
export async function createProperty(property: PropertyInsert, createdBy?: string): Promise<Property> {
  try {
    let userId = createdBy;
    
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('properties')
      .insert({
        ...property,
        created_by: userId,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating property:', error);
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error in createProperty:', error);
    throw error;
  }
}

/**
 * Update an existing property
 */
export async function updateProperty(
  id: string,
  updates: PropertyUpdate
): Promise<Property> {
  try {
    const { data, error } = await supabase
      .from('properties')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating property:', error);
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error in updateProperty:', error);
    throw error;
  }
}
